import DynamicsIcon from '../common/DynamicsIcon';
import ClientPage from '../common/ClientPage';
import React, { useState, useRef, useEffect } from 'react';
import { Send, User, AlertCircle } from 'lucide-react';
import backendApi from '../../services/backendApi';

const SUGGESTED_QUESTIONS = [
  'Quelles fonctionnalités sont disponibles dans mon application ?',
  "Qu'est-ce qui a été développé et livré jusqu'à présent ?",
  'Comment fonctionne la gestion des commandes dans l\'application ?',
  'Quelles sont les dernières fonctionnalités ajoutées au projet ?',
];

export default function ChatbotPage() {
  const projectId = localStorage.getItem('selected_project_id');
  const projectName = localStorage.getItem('selected_project_name') || `Projet #${projectId}`;

  const [messages, setMessages] = useState([{
    id: 'welcome',
    role: 'assistant',
    text: "Bonjour ! Je suis Dynamix, votre assistant applicatif. Posez-moi une question sur les fonctionnalités de votre projet — ce qu'il fait, comment ça fonctionne, ce qui a été développé — et je vous répondrai clairement, sans jargon technique.",
  }]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendQuestion = async (text) => {
    const q = (text || question).trim();
    if (!q || loading) return;

    if (!projectId) {
      setError('Aucun projet sélectionné. Veuillez choisir un projet avant de poser une question.');
      return;
    }

    setError(null);
    setQuestion('');
    setMessages(prev => [...prev, { id: Date.now(), role: 'user', text: q }]);
    setLoading(true);

    try {
      const data = await backendApi.askChatbot(q, projectId);
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', text: data.answer }]);
    } catch (err) {
      const msg = err?.message || 'Une erreur est survenue. Veuillez réessayer.';
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'error', text: msg }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <ClientPage title={<span className="flex items-center gap-3"><DynamicsIcon size={32} />Dynamix</span>} subtitle={projectId ? `Projet : ${projectName}` : 'Sélectionnez un projet pour commencer'}><div className="client-chat">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.map(msg => <ChatMessage key={msg.id} message={msg} />)}

        {loading && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
              <DynamicsIcon className="w-4 h-4 text-white" />
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3">
              <div className="flex gap-1 items-center h-5">
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Suggestions */}
      {messages.length === 1 && !loading && (
        <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SUGGESTED_QUESTIONS.map(q => (
            <button
              key={q}
              onClick={() => sendQuestion(q)}
              className="text-left text-sm text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl px-4 py-2.5 transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={e => { e.preventDefault(); sendQuestion(); }} className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder="Posez une question sur les fonctionnalités…"
          maxLength={500}
          disabled={loading}
          className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={loading || !question.trim()}
          className="px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center gap-2"
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline text-sm font-medium">Envoyer</span>
        </button>
      </form>

      <p className="text-xs text-gray-400 mt-2 text-center">
        Les réponses sont basées sur les fonctionnalités développées pour votre projet.
      </p>
    </div></ClientPage>
  );
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function renderMarkdown(text) {
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let keyCounter = 0;

  const flushList = () => {
    if (listItems.length === 0) return;
    elements.push(
      <ul key={keyCounter++} className="list-disc list-outside ml-5 space-y-1.5 my-2">
        {listItems.map((item, i) => (
          <li key={i} className="leading-relaxed">{renderInline(item)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      continue;
    }

    const listMatch = trimmed.match(/^[*-]\s+(.+)/);
    if (listMatch) {
      listItems.push(listMatch[1]);
      continue;
    }

    flushList();

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const cls = level === 1 ? 'font-bold text-base mt-3 mb-1' : 'font-semibold mt-2 mb-0.5';
      elements.push(<p key={keyCounter++} className={cls}>{renderInline(headingMatch[2])}</p>);
      continue;
    }

    elements.push(
      <p key={keyCounter++} className="mb-1 leading-relaxed">{renderInline(trimmed)}</p>
    );
  }

  flushList();
  return elements;
}

function ChatMessage({ message }) {
  if (message.role === 'user') {
    return (
      <div className="flex items-start gap-3 justify-end">
        <div className="bg-purple-600 text-white rounded-2xl rounded-tr-none px-4 py-3 text-sm max-w-[80%] whitespace-pre-wrap">
          {message.text}
        </div>
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-gray-600" />
        </div>
      </div>
    );
  }

  if (message.role === 'error') {
    return (
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-4 h-4 text-red-500" />
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl rounded-tl-none px-4 py-3 text-sm max-w-[80%]">
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
        <DynamicsIcon className="w-4 h-4 text-white" />
      </div>
      <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3 text-sm max-w-[80%] text-gray-800">
        {renderMarkdown(message.text)}
      </div>
    </div>
  );
}
